import {
  Button,
  DetailBox,
  DetailBoxColumn,
  Loading,
  Select,
  SelectOptionType,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Container, Row } from 'reactstrap';

import { AzureFilesUploaderControlled } from '../../../../components/AzureFilesUploader/AzureFileUploaderControlled';
import { FileRequiredTypes } from '../../../../components/AzureFilesUploader/types';
import GoBackToListButton from '../../../../components/buttons/GoBackToListButton';
import SectionTitle from '../../../../components/text/SectionTitle';
import { PathsLayouts } from '../../../../config/routes';
import { moneyFormatter } from '../../../../utils/currency';

import { createRefundCharge, listRefunds } from './api';
import DiscountSubForm from './components/ApplyRefund/DiscountSubForm';
import TransferSubForm from './components/ApplyRefund/TransferSubForm';
import {
  BankAccountType,
  CreateRefundChargePayload,
  Refund,
  RefundChargeType,
} from './types';

const dictPrefix =
  'financialPortal.currentAccount.tabs.currentAccount.applyRefund';

interface RouteParams {
  studentId: string;
  refundId: string;
}

interface FormShape {
  method: SelectOptionType | null;
  totalAmount: number | string;
  bankHolderName?: string;
  bankName?: string;
  bankAccountType?: SelectOptionType | null;
  bankAccountNumber?: string;
  bankContactEmail?: string;
  allocationsMap?: Record<string, number | string>;
  attachment?: { id: number; url: string } | null;
}

const EMPTY_FORM: FormShape = {
  method: null,
  totalAmount: '',
  bankHolderName: '',
  bankName: '',
  bankAccountType: null,
  bankAccountNumber: '',
  bankContactEmail: '',
  allocationsMap: {},
  attachment: null,
};

export default function ApplyRefundView() {
  const { t } = useTranslation();
  const history = useHistory();
  const { studentId: studentIdParam, refundId } = useParams<RouteParams>();
  const studentId = Number(studentIdParam);

  const [refund, setRefund] = useState<Refund | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const methods = useForm<FormShape>({
    defaultValues: EMPTY_FORM,
    mode: 'all',
  });
  const { control, watch, handleSubmit, reset } = methods;

  const methodOption = watch('method');
  const method = (methodOption?.value as RefundChargeType) ?? null;

  useEffect(() => {
    let mounted = true;
    listRefunds(studentId).then(({ data, error }) => {
      if (!mounted) return;
      if (error || !data) {
        addToast({
          text: t(`${dictPrefix}.errorOnLoad`),
          color: 'danger',
          icon: 'error',
        });
        setLoading(false);
        return;
      }
      const found = data.find((r) => r.id === refundId) ?? null;
      setRefund(found);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [studentId, refundId, t]);

  const refundAmount = useMemo(() => (refund ? Number(refund.amount) : 0), [
    refund,
  ]);

  const methodOptions = useMemo<SelectOptionType[]>(
    () => [
      {
        value: RefundChargeType.Transfer,
        label: t(`${dictPrefix}.method.transfer`),
      },
      {
        value: RefundChargeType.Discount,
        label: t(`${dictPrefix}.method.discount`),
      },
    ],
    [t],
  );

  const refundDetailColumns = useMemo<DetailBoxColumn[]>(() => {
    if (!refund) return [];
    return [
      {
        title: t(`${dictPrefix}.refundDetailBox.concept`),
        body: refund.concept,
        size: { xs: 12, md: 8 },
      },
      {
        title: t(`${dictPrefix}.refundDetailBox.amount`),
        body: moneyFormatter().format(Math.ceil(refundAmount)),
        size: { xs: 12, md: 4 },
      },
    ];
  }, [refund, refundAmount, t]);

  const handleCancel = useCallback(() => {
    reset(EMPTY_FORM);
    history.push(`${PathsLayouts.currentAccount}/${studentId}`);
  }, [history, studentId, reset]);

  const submitToApi = useCallback(
    async (payload: CreateRefundChargePayload) => {
      setSubmitting(true);
      const { error } = await createRefundCharge(refundId, payload);
      setSubmitting(false);

      if (error) {
        addToast({
          text: t(`${dictPrefix}.errors.createFailed`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      addToast({
        text: t(`${dictPrefix}.success`),
        color: 'success',
        icon: 'success',
      });
      history.push(`${PathsLayouts.currentAccount}/${studentId}`);
    },
    [refundId, history, studentId, t],
  );

  const onFinalize = useCallback(
    async (values: FormShape) => {
      if (!refund) return;
      if (!values.method?.value) {
        addToast({
          text: t(`${dictPrefix}.errors.methodRequired`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      const total = Number(values.totalAmount ?? 0);
      if (!Number.isFinite(total) || total <= 0) {
        addToast({
          text: t(`${dictPrefix}.errors.invalidTotal`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }
      if (total - refundAmount > 0.01) {
        addToast({
          text: t(`${dictPrefix}.errors.exceedsRefund`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      let payload: CreateRefundChargePayload;
      if (values.method.value === RefundChargeType.Transfer) {
        if (
          !values.bankHolderName?.trim() ||
          !values.bankName?.trim() ||
          !values.bankAccountType?.value ||
          !values.bankAccountNumber?.trim()
        ) {
          addToast({
            text: t(`${dictPrefix}.errors.transferFieldsRequired`),
            color: 'danger',
            icon: 'error',
          });
          return;
        }
        payload = {
          type: RefundChargeType.Transfer,
          totalAmount: total,
          attachmentUrl: values.attachment?.url ?? null,
          bankHolderName: values.bankHolderName.trim(),
          bankName: values.bankName.trim(),
          bankAccountType: values.bankAccountType.value as BankAccountType,
          bankAccountNumber: values.bankAccountNumber.trim(),
          bankContactEmail: values.bankContactEmail?.trim() || null,
        };
      } else {
        const allocations = Object.entries(values.allocationsMap ?? {})
          .map(([debtId, raw]) => ({ debtId, amount: Number(raw) }))
          .filter((a) => Number.isFinite(a.amount) && a.amount > 0);
        if (allocations.length === 0) {
          addToast({
            text: t(`${dictPrefix}.errors.noAllocations`),
            color: 'danger',
            icon: 'error',
          });
          return;
        }
        const sum = allocations.reduce((s, a) => s + a.amount, 0);
        if (Math.abs(sum - total) > 0.01) {
          addToast({
            text: t(`${dictPrefix}.errors.allocationsMismatch`),
            color: 'danger',
            icon: 'error',
          });
          return;
        }
        payload = {
          type: RefundChargeType.Discount,
          totalAmount: total,
          attachmentUrl: values.attachment?.url ?? null,
          allocations,
        };
      }

      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dictPrefix}.confirm.title`),
        subtitle: t(`${dictPrefix}.confirm.subtitle`),
        btnConfirm: {
          text: t(`${dictPrefix}.confirm.confirm`),
          onConfirm: () => submitToApi(payload),
        },
        btnClose: { text: t(`${dictPrefix}.confirm.back`) },
      });
    },
    [refund, refundAmount, t, submitToApi],
  );

  if (loading) {
    return (
      <Card className="mt-5 mx-3 p-5">
        <Loading textBody="" />
      </Card>
    );
  }

  if (!refund) {
    return (
      <Card className="mt-5 mx-3 p-5">
        <p>{t(`${dictPrefix}.notFound`)}</p>
        <GoBackToListButton
          path={`${PathsLayouts.currentAccount}/${studentId}`}
        />
      </Card>
    );
  }

  return (
    <Card className="mt-5 mx-3">
      <Container fluid className="p-4">
        <Row>
          <Col xs="auto" className="mb-3">
            <GoBackToListButton
              path={`${PathsLayouts.currentAccount}/${studentId}`}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col xs={12}>
            <SectionTitle text={t(`${dictPrefix}.title`)} />
            <p className="text-muted mb-0 mt-1">
              {t(`${dictPrefix}.subtitle`)}
            </p>
          </Col>
        </Row>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFinalize)}>
            <Row className="mb-4">
              <Col xs={12} md={6} className="mb-3">
                <DetailBox columns={refundDetailColumns} />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Select
                  name="method"
                  control={control}
                  label={t(`${dictPrefix}.method.label`)}
                  placeholder={t(`${dictPrefix}.method.placeholder`)}
                  options={methodOptions}
                  isSearchable={false}
                />
              </Col>
            </Row>

            {method === RefundChargeType.Transfer && (
              <TransferSubForm control={control} />
            )}
            {method === RefundChargeType.Discount && (
              <DiscountSubForm
                studentId={studentId}
                refundAmount={refundAmount}
              />
            )}

            {method && (
              <>
                <Row className="mt-4 mb-3">
                  <Col xs={12}>
                    <SectionTitle
                      text={t(`${dictPrefix}.attachment.sectionTitle`)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <AzureFilesUploaderControlled
                      name="attachment"
                      control={control}
                      fileType={FileRequiredTypes.RefundAttachment}
                      label={t(`${dictPrefix}.attachment.label`)}
                    />
                  </Col>
                </Row>
              </>
            )}

            <Row className="mt-5 pt-4">
              <Col xs={12} md={{ size: 6, offset: 6 }}>
                <Row>
                  <Col xs={6}>
                    <Button
                      type="button"
                      size="md"
                      text={t(`${dictPrefix}.actions.cancel`)}
                      outlined
                      fullwidth
                      disabled={submitting}
                      onClick={handleCancel}
                    />
                  </Col>
                  <Col xs={6}>
                    <Button
                      type="submit"
                      size="md"
                      text={t(`${dictPrefix}.actions.finalize`)}
                      fullwidth
                      loading={submitting}
                      disabled={!method || refundAmount <= 0}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Container>
    </Card>
  );
}
